import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { IAccountPermission, IApplicationPermission, IModulePermission, IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { EXCHANGE_RATES, PERMISSION_MAPPER, CurrencyCode, CURRENCY_SYMBOLS, CURRENCY_FORMATTERS } from "../shared/constants";
import {
  GoogleAdsAccount,
  IAccountsEntity,
} from "../features/Configurations/Integration/interface";
import dayjs from "dayjs";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function hasPermission(
  permission: string,
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  if (!permissions) return false;
  if (permissions === "*") return true;
  if (permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS) return true;
  if (typeof permissions === "string") {
    return permissions.includes(permission);
  }
  return (permissions[category as keyof IPermissionCategories] as IModulePermission[] | IApplicationPermission[] | IAccountPermission[])?.some((p) => p.codename === permission);
}

export function checkAllPermissions(
  permissionsToCheck: string[],
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  return permissionsToCheck.every((perm) =>
    hasPermission(perm, category, permissions)
  );
}

export function checkAnyPermissions(
  permissionsToCheck: string[],
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  return permissionsToCheck.some((perm) =>
    hasPermission(perm, category, permissions)
  );
}

export function checkPermission(
  permission: string,
  category: keyof IPermissionCategories,
  permissions?:
    | IPermissionCategories
    | PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    | string
) {
  return hasPermission(permission, category, permissions);
}

export const formatNumber = (value: number) => {
  // Round to 2 decimal places to handle floating-point precision issues
  const rounded = Math.round(value * 100) / 100;

  // Check if the rounded value is effectively an integer
  if (rounded % 1 === 0) {
    return rounded.toString();
  } else {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(rounded);
  }
};

// Utility function to convert GoogleAdsAccount from camelCase to snake_case
export const convertGoogleAdsAccountToEntity = (
  account: IAccountsEntity | GoogleAdsAccount | any
): IAccountsEntity => {
  return {
    id:
      (account.id ||
        (account as IAccountsEntity).publisher_id ||
        (account as GoogleAdsAccount).publisherId ||
        "") as string,
    name: account.name || "",
    currency_code:
      ((account as IAccountsEntity).currency_code ||
        (account as GoogleAdsAccount).currencyCode ||
        account.currency ||
        "") as string,
    publisher_id:
      (account as IAccountsEntity).publisher_id ||
      (account as GoogleAdsAccount).publisherId,
    reporting_time_zone:
      (account as IAccountsEntity).reporting_time_zone ||
      (account as GoogleAdsAccount).reportingTimeZone ||
      account.timeZone ||
      account.time_zone,
    status: ((account as IAccountsEntity).status ||
      (account as GoogleAdsAccount).status) as any,
  };
};

// Utility function to convert accounts array from GoogleAdsAccount to IAccountsEntity
export const convertAccountsToEntities = (
  accounts: GoogleAdsAccount[]
): IAccountsEntity[] => {
  return accounts.map((account) => convertGoogleAdsAccountToEntity(account));
};

export function getTimezoneOffsetString(timezoneStr: string): string {
  if (!timezoneStr) return "";
  const offsetMinutes = dayjs().tz(timezoneStr).utcOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
}


export const getNetworkWiseRevenue = (networkWiseReveue: { [key: string]: number }) => Object.entries(networkWiseReveue || {})
  .map(([network, amount]) => {
    const numericAmount = Number(amount);
    return {
      network,
      amount,
      numericAmount,
      isNumeric: Number.isFinite(numericAmount),
    };
  })
  .filter((e) => {
    if (!e.isNumeric) return true;
    return e.numericAmount !== 0;
  });

export const formatCurrency = (
  amount: number,
  currencyCode: CurrencyCode = "USD"
): string => {
  return CURRENCY_FORMATTERS[currencyCode]?.format(amount) || amount.toString();
};

export const convertCurrency = (
  amount: number,
  fromCurrency: CurrencyCode,
  toCurrency: CurrencyCode
): number => {
  // Convert from source currency to USD, then to target currency
  const amountInUSD = amount / EXCHANGE_RATES[fromCurrency];
  return amountInUSD * EXCHANGE_RATES[toCurrency];
};

export const getCurrencySymbol = (currencyCode: CurrencyCode = "USD"): string => {
  return CURRENCY_SYMBOLS[currencyCode] || "$";
};

// Reads a currency-specific numeric field from an object using the pattern `${prefix}_${currency}`.
// e.g. getAmountByCurrency(row, "spend", "AUD") → row.spend_aud ?? 0
export const getAmountByCurrency = (obj: Record<string, unknown>, prefix: string, currency: string): number => {
  return (obj?.[`${prefix}_${currency.toLowerCase()}`] as number) ?? 0;
};


// Date as YYYY-MM-DD in the user's local time. Don't use toISOString() for this: it converts to
// UTC first, so midnight in IST (UTC+5:30) becomes the previous day.
export const toApiDate = (date: Date): string => dayjs(date).format("YYYY-MM-DD");

// "?from=…&to=…&preset=…" so a page opened from a list (e.g. campaign detail) uses the same date range.
export const getDateRangeSearch = (dateRange: { startDate: Date; endDate: Date }, presetId?: string): string => {
  const params = new URLSearchParams({ from: toApiDate(dateRange.startDate), to: toApiDate(dateRange.endDate) });
  if (presetId) params.set("preset", presetId);
  return `?${params.toString()}`;
};

export const formatDate = (value: string | null | undefined, opts?: Intl.DateTimeFormatOptions): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, opts ?? { day: "numeric", month: "short", year: "numeric" });
};